import { HeroUIProvider, HeroUIProviderProps } from '@heroui/react';
import { MotionConfig } from 'framer-motion';
import { LucideProvider } from 'lucide-react';
import { ComponentProps, useSyncExternalStore } from 'react';

import { ToastProvider } from './Toast';

const noopSubscribe = () => () => undefined;

/* HeroUI's toast region portals into `document`, so it must only render on the
   client. This hydration check replaces the previous `next/dynamic` (`ssr: false`)
   so the package doesn't depend on Next.js and works in Vite apps. */
const ClientOnlyToastProvider = () => {
  const isHydrated = useSyncExternalStore(
    noopSubscribe,
    () => true,
    () => false,
  );

  return isHydrated ? <ToastProvider /> : null;
};

export const IconsProvider = (props: ComponentProps<typeof LucideProvider>) => {
  return <LucideProvider size={16} {...props} />;
};

/* `reducedMotion="user"` makes framer drop transform animations (the overlay scale in
   `OVERLAY_MOTION_PROPS`) when the client asks for no motion, keeping opacity fades. Besides being
   the accessible default, it is what lets Playwright click an option the moment a listbox opens:
   an animating panel is never "stable", so the retried click can land off-target and dismiss it. */
export const Provider = ({ children, ...props }: HeroUIProviderProps) => (
  <HeroUIProvider {...props}>
    <MotionConfig reducedMotion="user">
      <LucideProvider size={16}>
        <ClientOnlyToastProvider />
        {children}
      </LucideProvider>
    </MotionConfig>
  </HeroUIProvider>
);

export type { HeroUIProviderProps };

export { HeroUIProvider as BaseProvider };
